"use client";

import { PageHeader } from "@/components/ui/design-system/page-header";
import { Skeleton } from "@/components/ui/design-system/skeleton";
import { useCreatorTranslations } from "@/i18n/creator/useCreatorTranslations";
import { CARD_ROW } from "../one/ui";

/**
 * The feed before the requests arrive: the real grid, twelve cells, and inside each
 * the real card surface with a bone where each line of `RequestCard` goes
 * (eyebrow, figure and its word, two title lines, the clock and the count),
 * so the page does not jump when the data lands and the shape says what is
 * coming. The header above is the real one, since its words are static;
 * the program filter is left out, because a row of segments that then
 * vanishes for a one-program creator is the jump this exists to avoid.
 *
 * MIRRORS `RequestsFeed` AND `RequestCard`. Change the grid or the card
 * there and this is the file that has to move with it.
 */
export function RequestsFeedSkeleton() {
  const t = useCreatorTranslations("requests.feed");
  return (
    <div aria-busy="true">
      {/* A plain hidden line rather than role="status": see HomeSkeleton on
          why the skeleton must not answer the auth gate test's selector. */}
      <span className="sr-only">{t("loading")}</span>
      <ul
        aria-hidden
        className="m-0 grid list-none gap-nf-ds-4 p-0 sm:grid-cols-2 lg:grid-cols-3 lg:gap-nf-ds-5"
      >
        {/* Twelve: four full rows at a desk, enough to fill a tall window
            so the data landing fills in rather than fills out. A feed with
            fewer requests shrinks below the fold, which is the cheaper
            direction; one that grows after the skeleton is the jump. */}
        {Array.from({ length: 12 }, (_, index) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: static placeholder list
          <li key={index} className={`${CARD_ROW} flex flex-col gap-nf-ds-3`}>
            <Skeleton className="h-[10px] w-[64px]" />
            <div className="flex items-baseline gap-nf-ds-2">
              <Skeleton className="h-[26px] w-[84px]" />
              <Skeleton className="h-[14px] w-[72px]" />
            </div>
            {/* Alternate one- and two-line titles, as a real feed does. */}
            <div className="flex flex-col gap-nf-ds-1">
              <Skeleton className="h-[16px] w-[85%]" />
              {index % 2 === 1 ? (
                <Skeleton className="h-[16px] w-[55%]" />
              ) : null}
            </div>
            <div className="mt-auto flex items-center justify-between pt-nf-ds-1">
              <Skeleton className="h-[12px] w-[88px]" />
              <Skeleton className="h-[12px] w-[72px]" />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** The feed page's column, shared with `RequestsFeed`. */
export const REQUESTS_FEED_COLUMN =
  "mx-auto flex w-full max-w-[1120px] flex-1 flex-col gap-nf-ds-6 pt-nf-ds-4 lg:py-nf-ds-6";

/**
 * The whole feed page while the portal itself boots, for the shell
 * skeleton: the real header, since its words are static, over the grid.
 */
export function RequestsFeedPageSkeleton() {
  const t = useCreatorTranslations("requests.feed");
  return (
    <div className={REQUESTS_FEED_COLUMN}>
      <PageHeader title={t("title")} subtitle={t("subtitle")} />
      <RequestsFeedSkeleton />
    </div>
  );
}
